/**
 * Sonido sintetizado con Web Audio. Ni un fichero de audio en el bundle: un mp3
 * corto son 20-40 KB, y aqui hacen falta diez sonidos. Sintetizados son 0 bytes
 * y suenan igual de bien para lo que son.
 *
 * Dos cosas que hay que respetar en un movil:
 *
 *  1. El AudioContext NO se puede crear hasta que el usuario toque la pantalla.
 *     iOS y Android lo bloquean. Por eso `despertar()` se llama desde el primer
 *     toque real, no al cargar.
 *  2. Si el jugador silencia el movil o se va a otra app, hay que parar. El
 *     contexto se suspende solo, pero los osciladores que queden sonando se
 *     quedan colgados: cada sonido se programa con su final.
 */

type Forma = OscillatorType;

export class Audio {
  private ctx: AudioContext | null = null;
  private maestro: GainNode | null = null;
  private silenciado = false;

  /** Se llama desde el primer toque del usuario. Antes de eso no se puede. */
  despertar(): void {
    if (this.ctx) {
      // Si el navegador lo suspendio (cambio de app), reanudar.
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    try {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.maestro = this.ctx.createGain();
      this.maestro.gain.value = 0.32;
      this.maestro.connect(this.ctx.destination);
    } catch {
      // Sin audio el juego funciona igual. No vale la pena ni avisar.
      this.ctx = null;
    }
  }

  get activo(): boolean {
    return this.ctx !== null && !this.silenciado;
  }

  silenciar(valor: boolean): void {
    this.silenciado = valor;
    if (this.maestro) this.maestro.gain.value = valor ? 0 : 0.32;
  }

  /** Un tono con envolvente. El ladrillo del que salen casi todos los sonidos. */
  private tono(
    frec: number,
    dur: number,
    forma: Forma = 'square',
    volumen = 1,
    frecFinal?: number
  ): void {
    const ctx = this.ctx;
    const maestro = this.maestro;
    if (!ctx || !maestro || this.silenciado) return;

    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gan = ctx.createGain();
    osc.type = forma;
    osc.frequency.setValueAtTime(frec, t);
    if (frecFinal !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, frecFinal), t + dur);
    }
    // Ataque corto y caida exponencial: sin esto se oye un "clic" al cortar.
    gan.gain.setValueAtTime(0, t);
    gan.gain.linearRampToValueAtTime(volumen, t + 0.008);
    gan.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(gan);
    gan.connect(maestro);
    osc.start(t);
    // Se programa el final: un oscilador sin stop() se queda sonando para siempre.
    osc.stop(t + dur + 0.02);
  }

  /** Ruido blanco con filtro. Para los golpes y el deslizarse. */
  private ruido(dur: number, frecFiltro: number, volumen = 1): void {
    const ctx = this.ctx;
    const maestro = this.maestro;
    if (!ctx || !maestro || this.silenciado) return;

    const t = ctx.currentTime;
    const muestras = Math.floor(ctx.sampleRate * dur);
    const buffer = ctx.createBuffer(1, muestras, ctx.sampleRate);
    const datos = buffer.getChannelData(0);
    for (let i = 0; i < muestras; i++) datos[i] = Math.random() * 2 - 1;

    const fuente = ctx.createBufferSource();
    fuente.buffer = buffer;
    const filtro = ctx.createBiquadFilter();
    filtro.type = 'lowpass';
    filtro.frequency.value = frecFiltro;
    const gan = ctx.createGain();
    gan.gain.setValueAtTime(volumen, t);
    gan.gain.exponentialRampToValueAtTime(0.0001, t + dur);

    fuente.connect(filtro);
    filtro.connect(gan);
    gan.connect(maestro);
    fuente.start(t);
  }

  // --- Los sonidos del juego ---

  moneda(indiceCombo = 0): void {
    // La nota sube con las monedas seguidas: es lo que hace que apetezca
    // cogerlas todas en fila en vez de solo las que estan de paso.
    const escala = [880, 988, 1109, 1175, 1319, 1480, 1568];
    const i = Math.min(indiceCombo, escala.length - 1);
    this.tono(escala[i] as number, 0.09, 'square', 0.5);
  }

  salto(): void {
    this.tono(320, 0.14, 'sine', 0.6, 620);
  }

  deslizar(): void {
    this.ruido(0.22, 1400, 0.35);
  }

  golpe(): void {
    this.ruido(0.3, 500, 0.9);
    this.tono(130, 0.35, 'sawtooth', 0.55, 48);
  }

  escudoRoto(): void {
    this.tono(1200, 0.18, 'triangle', 0.5, 420);
    this.ruido(0.18, 3000, 0.3);
  }

  potenciador(): void {
    this.tono(660, 0.1, 'triangle', 0.5);
    window.setTimeout(() => this.tono(990, 0.14, 'triangle', 0.5), 70);
  }

  cohete(): void {
    this.ruido(0.8, 900, 0.5);
    this.tono(180, 0.7, 'sawtooth', 0.3, 700);
  }

  ballena(): void {
    // Lo mas parecido a un canto de ballena con un oscilador: barrido lento.
    this.tono(220, 1.1, 'sine', 0.55, 520);
    window.setTimeout(() => this.tono(330, 0.9, 'sine', 0.4, 180), 260);
  }

  /** Al pasar a otro bioma. Un acorde ascendente, corto. */
  cambioBioma(): void {
    const notas = [523, 659, 784];
    notas.forEach((n, i) => {
      window.setTimeout(() => this.tono(n, 0.22, 'triangle', 0.32), i * 80);
    });
  }

  /** Nuevo record. El unico sonido largo del juego: se lo ha ganado. */
  record(): void {
    const notas = [523, 659, 784, 1047];
    notas.forEach((n, i) => {
      window.setTimeout(() => this.tono(n, 0.3, 'square', 0.4), i * 110);
    });
  }

  /** "Casi lo logras": dos notas que se quedan a medias, a proposito. */
  casi(): void {
    this.tono(587, 0.18, 'triangle', 0.4);
    window.setTimeout(() => this.tono(494, 0.3, 'triangle', 0.35), 150);
  }

  botonUi(): void {
    this.tono(740, 0.05, 'square', 0.25);
  }
}

/** Instancia unica: el juego solo necesita una. */
export const audio = new Audio();
